import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import defaultPrompts from '../configs/defaultPrompts.json';

export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  prompt: string;
  category: 'function' | 'scenario';
  isDefault?: boolean;
  createdAt: number;
  updatedAt: number;
}

interface DocumentPromptSettings {
  functionTemplateId?: string;
  scenarioTemplateId?: string;
  customPrompt?: string;
}

interface PromptState {
  // 提示词模板
  templates: Record<string, PromptTemplate>;
  
  // 默认选择
  defaultFunctionTemplateId: string;
  defaultScenarioTemplateId: string;
  
  // 文档级提示词设置
  documentSettings: Record<string, DocumentPromptSettings>;
  
  // 操作方法
  setDefaultTemplates: (functionId: string, scenarioId: string) => void;
  setDocumentTemplates: (docId: string, functionId?: string, scenarioId?: string) => void;
  setDocumentCustomPrompt: (docId: string, prompt: string) => void;
  clearDocumentSettings: (docId: string) => void;
  createTemplate: (template: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateTemplate: (id: string, template: Partial<Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt'>>) => void;
  deleteTemplate: (id: string) => void;
  
  // 获取最终提示词
  getFinalPromptForDocument: (docId: string) => {
    systemPrompt: string;
    userPrompt: string;
  };
}

// 将默认模板转换为记录格式
const templatesRecord = defaultPrompts.templates.reduce<Record<string, PromptTemplate>>((acc, template) => {
  // 确保category字段类型正确
  const validTemplate: PromptTemplate = {
    ...template,
    category: template.category as 'function' | 'scenario'
  };
  acc[template.id] = validTemplate;
  return acc;
}, {});

// 创建带有持久化的store
export const usePromptStore = create<PromptState>()(
  persist(
    (set, get) => ({
      // 初始化状态从配置文件导入
      templates: templatesRecord,
      
      defaultFunctionTemplateId: defaultPrompts.defaultFunctionTemplateId,
      defaultScenarioTemplateId: defaultPrompts.defaultScenarioTemplateId,
      documentSettings: {},
      
      // 设置默认模板
      setDefaultTemplates: (functionId: string, scenarioId: string) => {
        set({
          defaultFunctionTemplateId: functionId,
          defaultScenarioTemplateId: scenarioId
        });
      },
      
      // 设置文档特定模板
      setDocumentTemplates: (docId: string, functionId?: string, scenarioId?: string) => {
        const state = get();
        const currentSettings = state.documentSettings[docId] || {};
        
        set({
          documentSettings: {
            ...state.documentSettings,
            [docId]: {
              ...currentSettings,
              ...(functionId !== undefined && { functionTemplateId: functionId }),
              ...(scenarioId !== undefined && { scenarioTemplateId: scenarioId })
            }
          }
        });
      },
      
      // 设置文档自定义提示词
      setDocumentCustomPrompt: (docId: string, prompt: string) => {
        const state = get();
        const currentSettings = state.documentSettings[docId] || {};
        
        set({
          documentSettings: {
            ...state.documentSettings,
            [docId]: {
              ...currentSettings,
              customPrompt: prompt
            }
          }
        });
      },
      
      // 清除文档设置（重置为默认）
      clearDocumentSettings: (docId: string) => {
        const state = get();
        const newDocumentSettings = { ...state.documentSettings };
        
        if (newDocumentSettings[docId]) {
          delete newDocumentSettings[docId];
          set({ documentSettings: newDocumentSettings });
        }
      },
      
      // 创建新模板
      createTemplate: (template: Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
        const state = get();
        const id = `template_${Date.now()}`;
        const now = Date.now();
        
        const newTemplate: PromptTemplate = {
          ...template,
          id,
          createdAt: now,
          updatedAt: now
        };
        
        set({
          templates: {
            ...state.templates,
            [id]: newTemplate
          }
        });
        
        return id;
      },
      
      // 更新模板
      updateTemplate: (id: string, template: Partial<Omit<PromptTemplate, 'id' | 'createdAt' | 'updatedAt'>>) => {
        const state = get();
        
        if (!state.templates[id]) {
          console.error(`Template with id ${id} not found.`);
          return;
        }
        
        set({
          templates: {
            ...state.templates,
            [id]: {
              ...state.templates[id],
              ...template,
              updatedAt: Date.now()
            }
          }
        });
      },
      
      // 删除模板
      deleteTemplate: (id: string) => {
        const state = get();
        
        if (!state.templates[id]) {
          console.error(`Template with id ${id} not found.`);
          return;
        }
        
        // 如果是默认模板，更新默认设置
        let newDefaultFunction = state.defaultFunctionTemplateId;
        let newDefaultScenario = state.defaultScenarioTemplateId;
        
        if (id === state.defaultFunctionTemplateId) {
          // 找到下一个同类型的模板或使用系统默认值
          newDefaultFunction = defaultPrompts.defaultFunctionTemplateId;
        }
        
        if (id === state.defaultScenarioTemplateId) {
          // 找到下一个同类型的模板或使用系统默认值
          newDefaultScenario = defaultPrompts.defaultScenarioTemplateId;
        }
        
        // 更新文档设置，移除对这个模板的引用
        const newDocumentSettings = { ...state.documentSettings };
        Object.keys(newDocumentSettings).forEach(docId => {
          const settings = newDocumentSettings[docId];
          if (settings.functionTemplateId === id) {
            settings.functionTemplateId = newDefaultFunction;
          }
          if (settings.scenarioTemplateId === id) {
            settings.scenarioTemplateId = newDefaultScenario;
          }
        });
        
        // 创建新模板对象，移除被删除的模板
        const newTemplates = { ...state.templates };
        delete newTemplates[id];
        
        set({
          templates: newTemplates,
          defaultFunctionTemplateId: newDefaultFunction,
          defaultScenarioTemplateId: newDefaultScenario,
          documentSettings: newDocumentSettings
        });
      },
      
      // 获取最终提示词组合
      getFinalPromptForDocument: (docId: string) => {
        const state = get();
        const docSettings = state.documentSettings[docId] || {};
        
        // 获取系统提示词 (固定不变)
        const systemPrompt = defaultPrompts.systemPrompt;
        
        // 获取功能模板
        const functionId = docSettings.functionTemplateId || state.defaultFunctionTemplateId;
        const functionTemplate = state.templates[functionId];
        
        // 获取情景模板
        const scenarioId = docSettings.scenarioTemplateId || state.defaultScenarioTemplateId;
        const scenarioTemplate = state.templates[scenarioId];
        
        // 组合用户提示词
        let userPrompt = '';
        if (scenarioTemplate) {
          userPrompt += scenarioTemplate.prompt + '\n\n';
        }
        if (functionTemplate) {
          userPrompt += functionTemplate.prompt + '\n\n';
        }
        if (docSettings.customPrompt) {
          userPrompt += docSettings.customPrompt;
        }
        
        return {
          systemPrompt,
          userPrompt: userPrompt.trim()
        };
      }
    }),
    {
      name: 'ai-editor-prompts',
      // 只持久化用户修改的部分，但保持完整的模板结构
      partialize: (state) => ({
        defaultFunctionTemplateId: state.defaultFunctionTemplateId,
        defaultScenarioTemplateId: state.defaultScenarioTemplateId,
        documentSettings: state.documentSettings,
        // 只保存用户创建的模板
        templates: Object.entries(state.templates).reduce((acc, [id, template]) => {
          if (!defaultPrompts.templates.some(t => t.id === id)) {
            acc[id] = template;
          }
          return acc;
        }, {} as Record<string, PromptTemplate>)
      }),
      // 在从存储中恢复状态后，确保默认模板始终存在
      onRehydrateStorage: () => (state) => {
        if (state) {
          // 确保所有默认模板都被加载
          const mergedTemplates = { ...templatesRecord };
          
          // 将用户自定义模板合并到默认模板中
          if (state.templates) {
            Object.entries(state.templates).forEach(([id, template]) => {
              mergedTemplates[id] = template;
            });
          }
          
          // 更新状态，确保默认模板始终存在
          state.templates = mergedTemplates;
          
          console.log("Store rehydrated with templates:", Object.keys(mergedTemplates).length);
        }
      }
    }
  )
); 